import React from "react";
import { Mail } from "lucide-react";
import { BUSINESS } from "../../lib/business";
import { useApp } from "../../context/AppContext";

/**
 * Global floating contact — two small round buttons stacked bottom-right :
 *   1. WhatsApp (green, links to the pre-filled quote request WA URL)
 *   2. Mail (navy, opens the default mail app)
 *
 * Design intent: DISCRETE on desktop (44×44), noticeably TAPPABLE on mobile
 * (52×52) so users can reach a real human easily on the phone. Both remain
 * visible on every scroll position.
 *
 * User preference (Feb 2026): NO auto-opening bubble, NO "call us" pill.
 * Just two clean round icons — one tap = WhatsApp, one tap = mail.
 */

// Official WhatsApp glyph — SVG matches the recognised worldwide icon.
const WhatsAppGlyph = ({ size = 22 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.001 2C6.478 2 2 6.478 2 12c0 1.786.474 3.51 1.371 5.05L2 22l5.116-1.34a10.005 10.005 0 004.887 1.253C17.526 21.913 22 17.435 22 12.001 22 6.477 17.526 2 12.001 2zm5.802 15.804a8.297 8.297 0 01-4.685 1.447c-1.523 0-3.02-.427-4.318-1.238l-.309-.183-3.208.84.858-3.126-.201-.32A8.259 8.259 0 013.7 12c0-4.574 3.72-8.295 8.301-8.295 2.215 0 4.297.863 5.864 2.43a8.246 8.246 0 012.43 5.865c0 2.213-.864 4.294-2.492 5.804z" />
  </svg>
);

export const FloatingContact = () => {
  const { t } = useApp();
  const waHref = BUSINESS.whatsapp(t.floating?.whatsappMessage || "Bonjour, je souhaite un devis AMB Élite.");
  const mailHref = `mailto:${BUSINESS.email}?subject=${encodeURIComponent(t.floating?.emailSubject || "Demande de devis AMB Élite")}`;

  return (
    <div
      data-testid="floating-contact"
      className="fixed bottom-5 right-4 md:bottom-6 md:right-5 z-50 flex flex-col items-end gap-2.5 md:gap-2"
    >
      {/* WhatsApp — official green, slightly larger on mobile for tap area */}
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp-btn"
        aria-label={t.floating?.whatsappAria || "Contacter AMB Élite par WhatsApp"}
        title={t.floating?.whatsappAria || "WhatsApp"}
        className="w-13 h-13 md:w-11 md:h-11 rounded-full flex items-center justify-center shadow-xl transition-transform duration-300 hover:scale-110 active:scale-95"
        style={{
          width: "clamp(44px, 12vw, 52px)",
          height: "clamp(44px, 12vw, 52px)",
          backgroundColor: "#25D366",
          color: "#FFFFFF",
          boxShadow:
            "0 6px 18px rgba(37, 211, 102, 0.35), inset 0 0 0 1px rgba(255,255,255,0.15)",
        }}
      >
        <WhatsAppGlyph size={22} />
      </a>

      {/* Mail — navy, matches the site palette */}
      <a
        href={mailHref}
        data-testid="floating-email-btn"
        aria-label={t.floating?.emailAria || "Contacter AMB Élite par email"}
        title={t.floating?.emailAria || "Email"}
        className="rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110 active:scale-95"
        style={{
          width: "clamp(40px, 11vw, 44px)",
          height: "clamp(40px, 11vw, 44px)",
          backgroundColor: "#0B1F3A",
          color: "#e6c368",
          boxShadow:
            "0 4px 14px rgba(11, 31, 58, 0.35), inset 0 0 0 1px rgba(230,195,104,0.28)",
        }}
      >
        <Mail size={18} strokeWidth={2.1} />
      </a>
    </div>
  );
};
